import * as React from "react";
import * as ReactDOM from "react-dom";
import { ITimeGroup } from "../../../models/get";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../../../components/toolbar";
import { InputSize } from "../../../components/input";
import { TextBox } from "../../../components/textBox";
import { Button, ButtonType, ButtonSize } from "../../../components/button";
import { Table, TextWithIconColumn, ITableColumn, ITableRowEventArgs } from "../../../components/table";
import SettingsNav from "../nav";
import RulesDialog from "./rulesDialog";
import { getSettingAsync, SettingKey } from "../../../globals";

interface IProperties
{
}

interface IState
{
    /**
     * Gibt an, ob gerade geladen wird.
     */
    isLoading?: boolean,

    /**
     * Das momentan selektierte TabItem.
     */
    selectedTabIndex?: number,

    /**
     * Gibt an, ob der Dialog geöffnet ist.
     */
    isDialogOpen?: boolean,

    /**
     * Gibt an, ob der Dialog zum bearbeiten eines Datensatzes geöffnet wird.
     */
    isDialogInEditMode?: boolean,

    /**
     * Enthält Text der zum Filtern der Liste verwendet wird.
     */
    filterValue?: string,

    /**
     * Enthält die geladenen Daten.
     */
    timeGroups?: ITimeGroup[],

    /**
     * Enthält die gefilterten Daten.
     */
    filteredTimeGroups?: ITimeGroup[],

    /**
     * Enthält den momentan selektierten Datensatz.
     */
    selectedItem?: ITimeGroup,

    /**
     * Enthält den momentan selektierten Zeilenindex.
     */
    selectedRowIndex?: string
}

export default class Rules extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see Rules.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: true
        }

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        var timeGroups = await getSettingAsync<ITimeGroup[]>(SettingKey.rulesCalendarsTimeGroups, true);

        this.setState({
            isLoading: false,
            timeGroups: timeGroups,
            filteredTimeGroups: timeGroups
        });
    }

    /**
     * Aktualisiert die Liste.
     */
    private async refreshAsync()
    {
        this.setState({
            isLoading: true,
            filterValue: null
        });

        await this.initAsync(this.props);
    }

    /**
     * Wird ausgeführt, sobald sich der Text der Filter TextBox ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     */
    private onFilterTextChanged = (value: any[], originalValue: string[]): void =>
    {
        const { timeGroups } = this.state;
        var filter: string = value[0];
        var filteredData: ITimeGroup[] = [];

        if (filter != null && filter !== "")
            filteredData = timeGroups.where(t => t.calendars_parsed.select(c => c.name).join(", ").toLowerCase().indexOf(filter.toLowerCase()) >= 0);
        else
            filteredData = timeGroups;

        this.setState({
            filterValue: value[0],
            filteredTimeGroups: filteredData
        });
    }

    /**
     * Wird ausgeführt sobald auf eine Zeile geklickt wird. Die Daten der ausgewählten
     * Zeile werden hier für weitere Zwecke zwischengespeichert.
     * @param e
     */
    private onRowClick = (e: ITableRowEventArgs<any>) =>
    {
        this.setState({
            selectedItem: e.dataSource,
            selectedRowIndex: e.index
        });
    }

    /**
     * Öffnet den Dialog zum erstellen eines neuen Datensatzes.
     */
    private onNewClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: false
        });
    }

    /**
     * Öffnet den Dialog zum bearbeiten eines Datensatzes.
     */
    private onEditClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: true
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     * @param refresh Angabe, ob die Ansicht aktualisiert werden soll.
     */
    private onDialogClosed = async (refresh: boolean) =>
    {
        if (refresh)
        {
            await this.refreshAsync();
            this.setState({
                isDialogOpen: false,
                selectedItem: null,
                selectedRowIndex: null
            });
        }
        else
        {
            this.setState({
                isDialogOpen: false
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich das selektierte TabItem ändert.
     * @param index Der neue Index.
     */
    private onTabChanged = (index: number) =>
    {
        this.setState((state: IState) =>
        {
            state.selectedTabIndex = index;
            return state;
        });
    }

    /**
     * Render einen InfoText wenn die Liste leer ist.
     */
    private renderInfoText(): JSX.Element
    {
        return (
            <div className="settings-default_empty">
                <i className="settings-default_empty_icon mdi mdi-lock"></i>
                <div className="settings-default_empty_header">Regeln</div>
                <div
                    style={{
                        width: 370
                    }}
                    className="settings-default_empty_text">
                    Erstellen Sie Regeln für Kalender die übergreifend nicht zur selben Zeit termininiert werden dürfen.
                </div>
                <Button
                    icon="plus"
                    text="Regel"
                    type={ButtonType.primary}
                    size={ButtonSize.big}
                    onClick={this.onNewClick} />
            </div>
        );
    }

    /**
     * Rendert die Tabelle.
     */
    private renderTable(): JSX.Element
    {
        const { timeGroups, filteredTimeGroups } = this.state;
        const columns: ITableColumn[] = [
            {
                header: "Regel",
                computedValue: (rule: ITimeGroup) => rule.calendars_parsed.select(c => c.name).join(", "),
                width: 500
            }
        ];

        if (timeGroups != null && timeGroups.length > 0)
        {
            return (
                <Table
                    style={{
                        position: "relative"
                    }}
                    dataSource={filteredTimeGroups}
                    columns={columns}
                    onRowClick={this.onRowClick} />
            );
        }
        else
        {
            return this.renderInfoText();
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isLoading, selectedTabIndex, isDialogOpen, isDialogInEditMode, filterValue, timeGroups, selectedItem } = this.state;
        const isListEmpty: boolean = timeGroups == null || timeGroups.length === 0;
        const isItemSelected: boolean = selectedItem != null;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1}
                    infoText="Regeln werden geladen" />
            );
        }
        
        return (
            <div
                className="settings-default-container"
                style={{
                    padding: 0
                }}>
                {
                    !isListEmpty &&
                    <div className="settings-default_toolbar">
                        <ToolBar hasTopBorder={false}>
                            <ToolBarItem
                                icon="plus"
                                text="Neu"
                                onClick={this.onNewClick} />
                            <ToolBarSeparator />
                            <ToolBarItem
                                icon="pencil"
                                text="Bearbeiten"
                                onClick={this.onEditClick}
                                isDisabled={!isItemSelected} />
                        </ToolBar>
                    </div>
                }
                {
                    !isListEmpty &&
                    <div className="settings-default_filter">
                        <TextBox
                            placeholder="Filter Regel..."
                            size={InputSize.normal}
                            value={filterValue}
                            onTextChange={this.onFilterTextChanged} />
                    </div>
                }
                <div className="settings-default">
                    {this.renderTable()}
                </div>
                {
                    isDialogOpen &&
                    <RulesDialog
                        rule={isDialogInEditMode ? selectedItem : null}
                        onClose={this.onDialogClosed} />
                }
            </div>
        );
    }
}